/**
 * WelcomeModal
 *
 * Auto-shows for first-time users after a 1-second delay.
 * Suppressed if the tour has already been seen, dismissed 3+ times,
 * a tour is already running, or the onboarding wizard is open.
 *
 * When the onboarding wizard closes the auto-show logic re-evaluates,
 * so the modal appears once the user can actually see the homepage.
 *
 * Uses a wasActive ref so the modal does not re-open when a tour
 * finishes (isActive transitions true → false).
 */

import { useEffect, useState, useRef } from 'react';
import { Sparkles, Clock, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTour } from '@/contexts/TourContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { getTourById } from '@/config/tours';

interface WelcomeModalProps {
  tourId: string;
  autoShow?: boolean;
}

export function WelcomeModal({ tourId, autoShow = true }: WelcomeModalProps) {
  const { shouldShowTour, startTour, updateTourProgress, isActive, isBackendSynced } = useTour();
  const { isWizardOpen: isOnboardingOpen } = useOnboarding();
  const [open, setOpen] = useState(false);
  const wasActive = useRef(false);

  const tour = getTourById(tourId);

  // Track when a tour is running so we suppress re-show on completion
  useEffect(() => {
    if (isActive) wasActive.current = true;
  }, [isActive]);

  // Auto-show after 1 second if eligible.
  // Suppressed while the onboarding wizard is open or backend sync hasn't completed;
  // re-evaluates when either changes.
  useEffect(() => {
    if (!autoShow) return;
    if (!tour) return;
    if (isOnboardingOpen) return;
    if (!isBackendSynced) return;

    const timer = setTimeout(() => {
      // Don't show if a tour is currently running, or just finished
      if (isActive || wasActive.current) return;
      if (!shouldShowTour(tourId)) return;
      setOpen(true);
    }, 1000);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboardingOpen, isBackendSynced]); // Re-run when onboarding wizard closes or backend sync completes

  if (!tour) return null;

  const durationMins = tour.estimatedDuration
    ? Math.max(1, Math.round(tour.estimatedDuration / 60))
    : null;

  const handleStart = () => {
    setOpen(false);
    startTour(tourId);
  };

  const handleDontShow = () => {
    setOpen(false);
    // Mark dismissed with count = 3 so shouldShowTour returns false permanently
    updateTourProgress(tourId, { status: 'dismissed', dismissalCount: 3 });
  };

  const handleRemindLater = () => {
    setOpen(false);
    updateTourProgress(tourId, { status: 'remind_later' });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="gap-2">
          <div className="flex items-center gap-2 text-primary">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
              <Sparkles className="h-5 w-5" />
            </div>
            <DialogTitle className="text-base">Welcome to Projects!</DialogTitle>
          </div>
          <DialogDescription className="text-sm leading-relaxed">
            This quick tour walks you through the key areas of your homepage — helping you
            get up to speed fast.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Duration badge */}
          {durationMins && (
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <Badge variant="secondary" className="text-xs font-normal">
                About {durationMins} minute{durationMins > 1 ? 's' : ''}
              </Badge>
            </div>
          )}

          {/* What you'll learn */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-foreground">What you will learn:</p>
            <ul className="space-y-1.5">
              {[
                'How to read your portfolio health at a glance',
                'Where to find projects that need attention',
                'How to customise your homepage layout',
                'Where to access help at any time',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-teal-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 pt-1">
          <Button className="w-full" onClick={handleStart}>
            Start Tour
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 text-sm"
              onClick={handleRemindLater}
            >
              Remind Me Later
            </Button>
            <Button
              variant="ghost"
              className="flex-1 text-sm text-muted-foreground"
              onClick={handleDontShow}
            >
              Don&apos;t Show Again
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
